import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const CustomerMasterEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '',
    gst: '',
    contactPerson: '',
    email: '',
    mobileNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    status: 'active'
  });

  useEffect(() => {
    if (id) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        API_URL,
        { action: 'getCustomer', customerId: id },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const customer = response.data;
      setFormData({
        companyName: customer.companyName || '',
        gst: customer.gst || '',
        contactPerson: customer.contactPerson || '',
        email: customer.email || '',
        mobileNumber: customer.mobileNumber || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        pincode: customer.pincode || '',
        status: customer.status || 'active'
      });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to load customer';
      toast.error(message);
      navigate('/customer-master');
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = useMemo(() => {
    return (
      formData.companyName.trim() !== '' &&
      formData.gst.trim() !== '' &&
      formData.contactPerson.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.mobileNumber.trim() !== '' &&
      formData.address.trim() !== '' &&
      formData.city.trim() !== '' &&
      formData.state.trim() !== '' &&
      formData.pincode.trim() !== ''
    );
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.companyName.trim()) {
      toast.error('Company Name is required.');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required.');
      return;
    }
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }

    const userId = localStorage.getItem('userid');
    
    const payload = {
      action: 'updateCustomer',
      customerId: id,
      companyName: formData.companyName.trim(),
      gst: formData.gst.trim(),
      contactPerson: formData.contactPerson.trim(),
      email: formData.email.trim(),
      mobileNumber: formData.mobileNumber.trim(),
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      pincode: formData.pincode.trim(),
      status: formData.status,
      updatedBy: userId
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(response.data?.message || 'Customer updated');
      navigate(`/customer-master-view/${id}`);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to update customer';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="employee-form">
        <div className="employee-loading">
          <LoadingIndicator size={120} />
        </div>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Edit Customer</h1>
            <p>Update customer information.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="profile-label" htmlFor="companyName">
                    Company Name <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    className="form-control"
                    placeholder="Enter company name"
                    value={formData.companyName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="profile-label" htmlFor="gst">
                    GST <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="gst"
                    name="gst"
                    className="form-control"
                    placeholder="Enter GST number"
                    value={formData.gst}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="profile-label" htmlFor="contactPerson">
                    Contact Person <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="contactPerson"
                    name="contactPerson"
                    className="form-control"
                    placeholder="Enter contact person name"
                    value={formData.contactPerson}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="profile-label" htmlFor="email">
                    Email <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="form-control"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="profile-label" htmlFor="mobileNumber">
                    Mobile Number <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="mobileNumber"
                    name="mobileNumber"
                    type="tel"
                    className="form-control"
                    placeholder="Enter mobile number"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label className="profile-label" htmlFor="address">
                    Address <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="address"
                    name="address"
                    className="form-control"
                    placeholder="Enter full address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="profile-label" htmlFor="city">
                    City <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="city"
                    name="city"
                    className="form-control"
                    placeholder="Enter city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="profile-label" htmlFor="state">
                    State <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="state"
                    name="state"
                    className="form-control"
                    placeholder="Enter state"
                    value={formData.state}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="profile-label" htmlFor="pincode">
                    Pincode <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="pincode"
                    name="pincode"
                    className="form-control"
                    placeholder="Enter pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* <div className="col-md-6">
                <div className="form-group">
                  <label className="profile-label" htmlFor="status">
                    Status <span className="required-asterisk">*</span>
                  </label>
                  <select
                    id="status"
                    name="status"
                    className="form-control"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div> */}
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingIndicator size={22} className="inline-loader" />
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerMasterEdit;
